<template lang="pug">
div(data-test="R_EventRosterImpl")
  component(is="style").
    tr[data-event-roster-highlight] > td {
      background-color: yellow;
    }
  AutoModal(:controller="emailLegacyFormLinkBuilderModalController" data-test="EmailLegacyFormLinkBuilderModal" style="max-height: 95vh;")
  MoveEventSignupModal(v-bind="moveEventSignupModalProps", v-on="moveEventSignupModalHandlers")
  ConfirmEventSignupCancellationModal(
    v-if="confirmEventSignupCancellationModalController.visible"
    v-bind="confirmEventSignupCancellationModalController.props"
    v-on="confirmEventSignupCancellationModalController.handlers"
  )
  div(class="hidden 2xl:block") Event roster for
  div.flex.items-center
    h1(class="lg:text-4xl self-end font-medium")
      span {{ event.eventName }}
    span.inline.cursor-pointer.p-1.rounded.ml-1(
      class="hover:bg-black/[.1] active:bg-black/[.15] text-[1em]"
      v-tooltip.top='{ content: `Edit this event` }'
    )
      router-link(
        :to='editEventRouteLocation',
      )
        font-awesome-icon(:icon='["fas", "pencil"]')

  form(class="p-1" @submit.prevent="() => { adultOrPlayerNameInput.committed = adultOrPlayerNameInput.input; }")
    label(class="flex items-center")
      input(type="checkbox" v-model="includeIncompleteSignups.value" data-test="includeIncompleteSignups")
      span(class="ml-1") Include incomplete signups
    label(class="flex items-center")
      input(type="checkbox" :checked="includeCanceled" data-test="includeCanceled" @input="() => $emit('update:includeCanceled', !includeCanceled)")
      span(class="ml-1") Include canceled signups
    div(class="flex")
      input(type="text" placeholder="filter by adult or player name..." class="w-full rounded-md" v-model="adultOrPlayerNameInput.input")
      t-btn(type="submit"  style="margin:0; margin-left:.25em;")
        div(class="text-xs") Filter
    div(
      v-if="adultOrPlayerNameInput.committed.trim() != ''"
      class="text-xs underline text-blue-700 mt-1 cursor-pointer"
      @click = "() => { adultOrPlayerNameInput.committed = adultOrPlayerNameInput.input = ''; }"
    ) Clear filter

  //- display:table is so the div grows to the width of the contained table
  div(class="shadow-md p-1 table w-full")
    div(class="flex items-center gap-2")
      BulkActionsButton(
        class="my-2"
        :eventID="eventID"
        :selectedEventSignups="selectedEventSignups"
        :generatingXLSXFile="generatingXLSXFile"
        @bulkAddLabels="$emit('bulkAddLabels')"
        @bulkRemoveLabels="$emit('bulkRemoveLabels')"
        @bulkAddPlayersAsEligibleToRegister="$emit('bulkAddPlayersAsEligibleToRegister')"
        @bulkSelectByLabel="$emit('bulkSelectByLabel')"
        @bulkDownloadAsXLSX="handleBulkDownloadAsXLSXRequest"
        @emailParticipants="handleEmailParticipants"
      )
      div(v-if="bulkSelectionsNotContainedInFilteredSignups == 1" class="text-red-700")
        | Note: 1 signup is filtered away but is also bulk selected
      div(v-else-if="bulkSelectionsNotContainedInFilteredSignups > 1" class="text-red-700")
        | Note: {{ bulkSelectionsNotContainedInFilteredSignups }} signups are filtered away but are also bulk selected
    BasicAutoTable2(
      v-bind="tableProps"
    )

  //-
  //- misc. UI things
  //-
  div.text-xs
    div.mt-2

    div.mt-2.inline-block.divide-y
      div
        ForceAddSignupSearcher(v-bind="userSearchProps" v-on="userSearchHandlers" v-slot="{hasResults, clearResults}" data-test="forceAddUser")
          div
            div Add user
            div(v-if="hasResults" @click="clearResults").text-xs.text-blue-700.cursor-pointer Clear Search
      div.mt-2.pt-2
        ForceAddSignupSearcher(v-bind="playerSearchProps" v-on="playerSearchHandlers" v-slot="{hasResults, clearResults}" data-test="forceAddPlayer")
          div
            div Add player
            div(v-if="hasResults" @click="clearResults").text-xs.text-blue-700.cursor-pointer Clear Search
</template>

<script lang="tsx">
import { computed, defineComponent, reactive, ref } from "vue"
import * as ilapi from "src/composables/InleagueApiV1"
import * as iltypes from "src/interfaces/InleagueApiV1"

import { axiosInstance } from "src/boot/axios";


import * as R_EventEditor from "./R_EventEditor.ilx";
import { SignupColumnRenderer, EventSignupColDefs, colDefsAsMap, C_TinySignupsListing, QuestionAnswersListing, maybeGetVolunteerPointsLink, downloadEventSignupsAsXLSX } from "./R_EventRosterImpl.iltsx"
import { EventRosterEmailLegacyFormLink } from "./EventRoster.emailLegacyFormLink"
import { ExpandedEvent, ExpandedEventTrackerState, EventRosterListingEventBus, BulkSelectedEventSignupTracker } from "./R_EventRosterImpl.ilx"

import { ForceAddSignupSearcher as C_ForceAddSignupSearcher } from "./EventRosterForceAddSignupSearcher";
import * as M_ForceAddSignupSearcher from "./EventRosterForceAddSignupSearcher"

import { propsDef } from "./R_EventRosterImpl.ilx"

import { accentAwareCaseInsensitiveCompare, sortByMany, useIziToast, vReqT } from "src/helpers/utils";

import C_ConfirmEventSignupCancellationModal from "./ConfirmEventSignupCancellationModal.vue"
import * as M_ConfirmEventSignupCancellationModal from "./ConfirmEventSignupCancellationModal.ilx"

import * as MoveEventSignupModal from "./MoveEventSignupModal"
import { Btn2 } from "../UserInterface/Btn2";
import { AutoModal, DefaultModalController_r, Modal } from "../UserInterface/Modal";
import { freshSortState } from "src/modules/TableUtils";
import { BasicAutoTable2, typedBasicAutoTable2Props } from "src/modules/BasicAutoTable";
import { EventSignup } from "src/composables/InleagueApiV1.Event";
import { Menu, MenuItem } from "../Tournaments/Menu";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { authZ_eventRoster_bulkAddPlayersAsEligibleToRegister } from "./R_EventRoster.route";
import { SoccerBall } from "../SVGs";

const BulkActionsButton = defineComponent({
  props: {
    eventID: vReqT<iltypes.Guid>(),
    selectedEventSignups: vReqT<BulkSelectedEventSignupTracker>(),
    generatingXLSXFile: vReqT<boolean>(),
  },
  emits: {
    bulkAddPlayersAsEligibleToRegister: () => true,
    bulkAddLabels: () => true,
    bulkRemoveLabels: () => true,
    bulkSelectByLabel: () => true,
    bulkDownloadAsXLSX: () => true,
    emailParticipants: () => true,
  },
  setup(props, ctx) {
    const buttonClasses = "hover:bg-slate-100 active:bg-slate-200 group flex w-full text-left rounded-md px-2 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-600"
    const volunteerPointsLink = computed(() => {
      return maybeGetVolunteerPointsLink(
        props.eventID,
        [...props.selectedEventSignups.selected.values()]
      )
    })

    const whatIsSelected = computed(() => {
      const selected = [...props.selectedEventSignups.selected.values()]
      return {
        atLeastOne: selected.length > 0,
        atLeastOnePlayerSignup: selected.some(v => !!v.childID)
      }
    })

    return () => {
      return <Menu data-test="bulkActionsMenu" anchor="left" transitionOrigin="origin-top-left" width={"w-64"}>
        {{
          button: () => (
            <Btn2 class="px-2 py-1 flex items-center gap-2" data-test="actions" disabled={props.generatingXLSXFile}>
              {props.generatingXLSXFile
                ? <SoccerBall color="gray"/>
                : <FontAwesomeIcon icon={faEllipsisVertical}/>}
              <span class="flex items-center gap-1">
                <span>Bulk Actions</span>
                <span class="text-xs">{props.selectedEventSignups.selected.size > 0 ? `(${props.selectedEventSignups.selected.size})` : ""}</span>
              </span>
            </Btn2>
          ),
          menuItems: () => <div class="font-normal">
            <MenuItem class="my-1"><button onClick={() => ctx.emit("bulkSelectByLabel")} class={buttonClasses} type="button">Select by labels</button></MenuItem>
            <MenuItem class="my-1"><button disabled={props.selectedEventSignups.selected.size === 0} onClick={() => ctx.emit("bulkAddLabels")} class={buttonClasses} type="button">Add labels</button></MenuItem>
            <MenuItem class="my-1"><button disabled={props.selectedEventSignups.selected.size === 0} onClick={() => ctx.emit("bulkRemoveLabels")} class={buttonClasses} type="button">Remove labels</button></MenuItem>
            {authZ_eventRoster_bulkAddPlayersAsEligibleToRegister()
              ? <MenuItem class="my-1">
                <button
                  disabled={!whatIsSelected.value.atLeastOnePlayerSignup}
                  onClick={() => ctx.emit("bulkAddPlayersAsEligibleToRegister")}
                  class={buttonClasses}
                  type="button"
                >
                  Make selected players eligible for registration
                </button>
              </MenuItem>
              : null}
            <MenuItem class="my-1">
              <button disabled={!volunteerPointsLink.value} class={buttonClasses} type="button">
                <a
                  href={volunteerPointsLink.value ?? "javascript:void(0)"}
                  target={volunteerPointsLink.value ? "_blank" : undefined}
                  style="cursor:inherit;" // inherit button cursor style, based on disabled or not
                >
                  Send selected users to Volunteer Points Assignment page
                </a>
              </button>
            </MenuItem>
            <MenuItem class="my-1">
              <button
                disabled={props.selectedEventSignups.selected.size === 0}
                onClick={() => ctx.emit("bulkDownloadAsXLSX")}
                class={buttonClasses}
                data-test="downloadAsXLSX-button"
                type="button"
              >
                Download selected as XLSX
              </button>
            </MenuItem>
            <MenuItem class="my-1">
              <button
                disabled={!whatIsSelected.value.atLeastOne}
                onClick={() => ctx.emit("emailParticipants")}
                class={buttonClasses}
                type="button"
                data-test="sendEmail-button"
              >
                Email Selected
              </button>
            </MenuItem>
          </div>
        }}
      </Menu>
    }
  }
})

export default defineComponent({
  components: {
    EventRosterEmailLegacyFormLink,
    SignupColumnRenderer,
    ForceAddSignupSearcher: C_ForceAddSignupSearcher,
    TinySignupsListing: C_TinySignupsListing,
    ConfirmEventSignupCancellationModal: C_ConfirmEventSignupCancellationModal,
    MoveEventSignupModal: MoveEventSignupModal.MoveEventSignupModal,
    QuestionAnswersListing,
    Btn2,
    AutoModal,
    BasicAutoTable2,
    BulkActionsButton,
  },
  props: propsDef,
  emits: {
    bulkAddLabels: () => true,
    bulkRemoveLabels: () => true,
    bulkAddPlayersAsEligibleToRegister: () => true,
    editLabels: (_: EventSignup) => true,
    "update:includeCanceled": (_: boolean) => true,
    bulkSelectByLabel: () => true,
    editComments: (_: EventSignup) => true,
  },
  setup(props, ctx) {
    const iziToast = useIziToast();




    const expandedEventTrackerState = computed(() => {
      return ExpandedEventTrackerState(axiosInstance, props.expandedEvent);
    });

    const event = computed<ExpandedEvent>(() => {
      return expandedEventTrackerState.value.expandedEvent.value
    });

    const userSearchProps : M_ForceAddSignupSearcher.Props = reactive({
      entitiesHavingSignups: computed(() : Set<string> => expandedEventTrackerState.value.usersHavingActiveSignups.value),
      entitySearcher: async (search: string) => {
        return (await ilapi.findVolunteers(axiosInstance, {search}))
        .sort(sortByMany(
            (l,r) => accentAwareCaseInsensitiveCompare(l.lastName, r.lastName),
            (l,r) => accentAwareCaseInsensitiveCompare(l.firstName, r.firstName),
          ))
          .map(result => {
            return {
              name: `${result.firstName} ${result.lastName}`,
              entityID: result.ID
            }
          });
      },
    });

    const userSearchHandlers : M_ForceAddSignupSearcher.Emits = {
      entitySelected: async (entityID) => {
        if (expandedEventTrackerState.value.usersHavingActiveSignups.value.has(entityID)) {
          // shouldn't happen
          return;
        }
        await expandedEventTrackerState.value.forceCreateActiveUnpaidSignup(entityID, "user");
      }
    }

    const playerSearchProps : M_ForceAddSignupSearcher.Props = reactive({
      entitiesHavingSignups: computed(() : Set<string> => expandedEventTrackerState.value.playersHavingActiveSignups.value),
      entitySearcher: async (search: string) => {
        return (await ilapi.findPlayers(axiosInstance, {search}))
          .sort(sortByMany(
            (l,r) => accentAwareCaseInsensitiveCompare(l.playerLastName, r.playerLastName),
            (l,r) => accentAwareCaseInsensitiveCompare(l.playerFirstName, r.playerFirstName),
          ))
          .map(result => {
            return {
              name: `${result.playerFirstName} ${result.playerLastName}`,
              entityID: result.childID
            }
          });
      },
    })

    const playerSearchHandlers : M_ForceAddSignupSearcher.Emits = {
      entitySelected: async (entityID) => {
        if (expandedEventTrackerState.value.playersHavingActiveSignups.value.has(entityID)) {
          // shouldn't happen
          return;
        }
        await expandedEventTrackerState.value.forceCreateActiveUnpaidSignup(entityID, "player");
      }
    }

    const eventRosterListingBus = (() => {
      const ret = new EventRosterListingEventBus();
      ret.onCancelSignup(eventSignup => confirmEventSignupCancellationModalController.show(eventSignup));
      ret.onMoveSignup(eventSignup => {
        isModalFocus[eventSignup.eventSignupID] = true;
        moveEventSignupModalProps.value = {
          isOpen: true,
          eventSignup
        }
      });
      ret.onRequestEditLabels(signup => ctx.emit("editLabels", signup))
      ret.onRequestEditComments(signup => ctx.emit("editComments", signup))
      return ret;
    })();

    const colDefs = EventSignupColDefs(eventRosterListingBus, expandedEventTrackerState, props.selectedEventSignups, props.labelFilterOptions, props.labelFilterSelections);
    const sortState = freshSortState(colDefs)
    sortState.reconfigure([{colID: "name", dir: "asc"}])
    const colDefsMap = computed(() => {
      return colDefsAsMap(colDefs);
    })

    const tableProps = computed(() => {
      return typedBasicAutoTable2Props<EventSignup>({
        colDefs: colDefs,
        rows: props.filteredSignups,
        sortState: sortState,
        noData: () => <div>No data</div>,
        paginationOptions: [25, 50, "ALL"],
        rowKey: v => v.eventSignupID,
        rowAttrs: v => {
          return {
            "data-test": `entityID=${v.entityID}`,
            "data-event-roster-highlight": props.highlightedEventSignups.has(v.eventSignupID) ? "" : null
          }
        }
      })
    })

    const isModalFocus = reactive<{[eventID: iltypes.Guid]: boolean}>({})

    const confirmEventSignupCancellationModalController = M_ConfirmEventSignupCancellationModal.DefaultController({
      handlers: {
        confirmCancellation: async (eventSignup) => {
          await expandedEventTrackerState.value.cancelEventSignup(eventSignup);
          confirmEventSignupCancellationModalController.hide();
        },
        doNotCancel: () => {
          confirmEventSignupCancellationModalController.hide();
        }
      },
      preShow: (props) => {
        isModalFocus[props.eventSignup.eventSignupID] = true;
      },
      preHide: (props) => {
        delete isModalFocus[props.eventSignup.eventSignupID];
      }
    })

    const moveEventSignupModalProps = ref<MoveEventSignupModal.Props>({isOpen: false, eventSignup: null})
    const moveEventSignupModalHandlers : MoveEventSignupModal.Emits = {
      close: () => {
        delete isModalFocus[
          // `eventSignup` is expected to be definitely truthy here -- can't be being shown unless it's truthy
          moveEventSignupModalProps.value.eventSignup!.eventSignupID
        ];
        moveEventSignupModalProps.value = {
          isOpen: false,
          // either we shouldn't ever set this back to null, or we need a smarter "set to null after it's really closed and all the animation is done"
          // because it looks jarring to clear this, while the close animation is playing
          eventSignup: moveEventSignupModalProps.value.eventSignup
        }
      },
      doMove: async ({eventSignup, freshEventID, comments}) => {
        const result = await expandedEventTrackerState.value.moveEventSignup({eventSignup, moveToThisEventID: freshEventID, comments});
        if (result.ok) {
          moveEventSignupModalHandlers.close();
        }
        else {
          // no-op; we assume axios error handlers have issued any releveant toasts
        }
      }
    }

    const emailLegacyFormLinkBuilderModalController = (() => {
      return DefaultModalController_r<{signups: EventSignup[]}>({
        title: Modal.simpleTitle("Email Participants"),
        content: data => {
          if (!data) {
            return null
          }

          return <div class="my-2">
            <EventRosterEmailLegacyFormLink
              event={expandedEventTrackerState.value.expandedEvent.value}
              signups={data.signups}
              onComplete={() => emailLegacyFormLinkBuilderModalController.close()}
            />
          </div>
        }
      })
    })();

    const generatingXLSXFile = ref(false)

    return {
      colDefs,
      colDefsMap,

      editEventRouteLocation: R_EventEditor.routeDetailToRoutePath({name: R_EventEditor.RouteName.edit, eventID: event.value.eventID}),

      expandedEventTrackerState: expandedEventTrackerState,
      event,

      userSearchProps,
      userSearchHandlers,
      playerSearchProps,
      playerSearchHandlers,

      confirmEventSignupCancellationModalController,

      moveEventSignupModalProps,
      moveEventSignupModalHandlers,
      isModalFocus,
      emailLegacyFormLinkBuilderModalController,
      tableProps,
      eventID: computed(() => props.expandedEvent.eventID),
      generatingXLSXFile,
      handleEmailParticipants: () => {
        const selected = [...props.selectedEventSignups.selected.values()]
        emailLegacyFormLinkBuilderModalController.open({signups: selected})
      },
      handleBulkDownloadAsXLSXRequest: async () => {
        try {
          generatingXLSXFile.value = true
          const signups = [...props.selectedEventSignups.selected.values()].sort(sortState.asSorter())
          await downloadEventSignupsAsXLSX(colDefs, signups, event.value.rosterLayoutDetail);
        }
        finally {
          generatingXLSXFile.value = false
        }
      }
    }
  }
})

</script>

<style scoped>
th, td {
  border-collapse: collapse;
  border: 1px solid #DDD;
}
th, td {
  padding:.25em;
  vertical-align: top;
}

tr:first-child th {
  border-top: none;
}

tr > th:first-child, tr > td:first-child {
  border-left: none;
}

tr > th:last-child, tr > td:last-child {
  border-right: none;
}

tr:last-child > td {
  border-bottom: none;
}

tr.group-sep:not(:last-child) > td {
  border: 1px solid #555;
}
</style>
